import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Loader2, Plus, Trash2, Upload, Wallet } from "lucide-react";
import { api, dt, errMsg, exportCsv, rp } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const Field = ({ label, ...props }) => (
  <div className="space-y-2">
    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{label}</Label>
    <Input className="h-11 rounded-xl bg-secondary/50" {...props} />
  </div>
);

const PackagePicker = ({ packages, value = [], onChange, role, testId = "pkg-picker" }) => {
  const priceKey = role === "mitra" ? "price_mitra" : "price_user";
  const toggle = (profile) => {
    const set = new Set(value);
    if (set.has(profile)) set.delete(profile); else set.add(profile);
    onChange([...set]);
  };
  return (
    <div className="space-y-2 sm:col-span-2" data-testid={testId}>
      <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Paket Voucher yang Boleh Dijual</Label>
      {packages.length === 0 ? (
        <p className="text-[11px] text-muted-foreground">Belum ada paket voucher. Tambahkan dulu di menu Setting → Paket Voucher.</p>
      ) : (
        <>
          <div className="grid max-h-48 gap-1.5 overflow-y-auto rounded-xl border border-border bg-secondary/30 p-2 sm:grid-cols-2">
            {packages.map((p) => {
              const active = value.includes(p.profile);
              return (
                <button
                  key={p.profile} type="button" onClick={() => toggle(p.profile)} data-testid={`pkg-${p.profile}`}
                  className={`flex items-center justify-between gap-2 rounded-lg border px-2.5 py-2 text-left text-xs transition-colors ${active ? "border-cyan-500/60 bg-cyan-500/10" : "border-border hover:bg-secondary"}`}
                >
                  <span className="truncate">
                    <span className="font-medium">{p.profile}</span>
                    <span className="ml-1 font-mono text-[10px] text-muted-foreground">{rp(p[priceKey] || 0)}</span>
                  </span>
                  <span className={`h-3.5 w-3.5 shrink-0 rounded-full border ${active ? "border-cyan-400 bg-cyan-400" : "border-muted-foreground/40"}`} />
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" size="sm" variant="outline" className="h-7 rounded-full text-[11px]" onClick={() => onChange(packages.map((p) => p.profile))} data-testid={`${testId}-all`}>Pilih semua</Button>
            <Button type="button" size="sm" variant="outline" className="h-7 rounded-full text-[11px]" onClick={() => onChange([])} data-testid={`${testId}-none`}>Kosongkan</Button>
            <span className="text-[11px] text-muted-foreground">{value.length ? `${value.length} paket dipilih` : "kosong = ikut pengaturan global"}</span>
          </div>
        </>
      )}
    </div>
  );
};

export default function Accounts({ role = "mitra" }) {
  const title = role === "mitra" ? "Mitra" : role === "admin" ? "Admin" : "User";
  const { user: me } = useAuth();
  const isSuper = me?.role === "superadmin";
  const [tab, setTab] = useState("list");
  const [rows, setRows] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [routers, setRouters] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [busy, setBusy] = useState(false);
  const [packages, setPackages] = useState([]);
  const [form, setForm] = useState({ username: "", password: "", name: "", phone: "", saldo: 0, router_id: "", owner_admin_id: "", allowed_packages: [] });
  const [edit, setEdit] = useState(null);
  const [saldo, setSaldo] = useState({ uid: null, amount: 0, note: "" });
  const [imp, setImp] = useState({ open: false, file: null, update: false, result: null, preview: null, previewing: false });

  const load = async () => {
    try {
      const [a, d, r, ad, st] = await Promise.all([
        api.get(`/accounts?role=${role}`),
        api.get("/deposits"),
        api.get("/routers").catch(() => ({ data: [] })),
        api.get("/admins").catch(() => ({ data: [] })),
        api.get("/settings").catch(() => ({ data: {} })),
      ]);
      setPackages((st.data?.packages || []).filter((p) => String(p.profile || "").trim()));
      setRows(a.data);
      setDeposits(d.data.filter((x) => (role === "admin" ? true : x.role === role)));
      setRouters(r.data);
      setAdmins(ad.data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  useEffect(() => { load(); setTab("list"); /* eslint-disable-next-line */ }, [role]);

  const create = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/accounts", { ...form, role, saldo: Number(form.saldo) || 0 });
      toast.success(`${title} ${form.username} dibuat`);
      setForm({ username: "", password: "", name: "", phone: "", saldo: 0, router_id: "", owner_admin_id: "", allowed_packages: [] });
      setTab("list"); load();
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  const save = async () => {
    const newUsername = String(edit.username || "").trim().toLowerCase();
    if (!newUsername) return toast.error("Username tidak boleh kosong");
    if (newUsername !== edit._username0 && !/^[a-z0-9._-]{3,20}$/.test(newUsername)) {
      return toast.error("Username 3-20 karakter: huruf kecil, angka, titik, garis bawah, atau strip");
    }
    setBusy(true);
    try {
      await api.put(`/accounts/${edit.id}`, {
        name: edit.name, phone: edit.phone, status: edit.status,
        ...(newUsername !== edit._username0 ? { username: newUsername } : {}),
        ...(role === "admin" ? { router_id: edit.router_id || "" } : {}),
        ...(role !== "admin" && isSuper && edit.owner_admin_id !== edit._owner0 ? { owner_admin_id: edit.owner_admin_id || "" } : {}),
        ...(role === "mitra" || role === "user" ? { allowed_packages: edit.allowed_packages || [] } : {}),
        ...(edit.newPassword ? { password: edit.newPassword } : {}),
      });
      toast.success(newUsername !== edit._username0 ? `Username diubah menjadi ${newUsername}` : "Perubahan disimpan");
      setEdit(null); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const previewCsv = async (file, update) => {
    if (!file) return;
    setImp((c) => ({ ...c, previewing: true, preview: null, result: null }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("role", role);
      fd.append("update_existing", (update ?? imp.update) ? "true" : "false");
      fd.append("dry_run", "true");
      const { data } = await api.post("/accounts/import-csv", fd, { timeout: 300000 });
      setImp((c) => ({ ...c, preview: data }));
    } catch (e) { toast.error(errMsg(e)); } finally { setImp((c) => ({ ...c, previewing: false })); }
  };

  const importCsv = async () => {
    if (!imp.file) return toast.error("Pilih file CSV dulu");
    setBusy(true);
    setImp((c) => ({ ...c, result: null }));
    try {
      const fd = new FormData();
      fd.append("file", imp.file);
      fd.append("role", role);
      fd.append("update_existing", imp.update ? "true" : "false");
      const { data } = await api.post("/accounts/import-csv", fd, { timeout: 300000 });
      setImp((c) => ({ ...c, result: data }));
      toast.success(`Impor selesai: ${data.created} akun baru, ${data.updated} diperbarui`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/accounts/import-template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `template-${role}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (row) => {
    try { await api.delete(`/accounts/${row.id}`); toast.success(`${row.username} dihapus`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const topup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/accounts/${saldo.uid}/saldo`, { amount: Number(saldo.amount), note: saldo.note });
      toast.success(`Saldo diperbarui: ${rp(data.saldo)}`);
      setSaldo({ uid: null, amount: 0, note: "" }); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const actDeposit = async (d, action) => {
    try { await api.post(`/deposits/${d.id}/action`, { action }); toast.success(`Permintaan ${action === "approve" ? "disetujui" : "ditolak"}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Tabs value={tab} onValueChange={setTab} className="fade-up space-y-6">
      <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 rounded-xl bg-secondary/60 p-1">
        {[["list", `Daftar ${title}`], ["add", `Add ${title}`], ["saldo", "Add Saldo"], ["deposit", "Permintaan Saldo"]].map(([v, l]) => (
          <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="rounded-lg px-4 py-2 text-xs data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-500/20 data-[state=active]:to-violet-600/20">{l}</TabsTrigger>
        ))}
      </TabsList>

      <TabsContent value="list">
        <div className="mb-4 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">{rows.length} {title.toLowerCase()} terdaftar</p>
          <div className="flex gap-2">
            <Button size="sm" className="rounded-full bg-gradient-to-r from-emerald-500 to-cyan-600 text-white" onClick={() => setImp({ open: true, file: null, update: false, result: null })} data-testid="open-import-accounts">
              <Upload className="mr-1 h-3.5 w-3.5" /> Impor CSV
            </Button>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => exportCsv(`${role}.csv`, rows)} data-testid="export-accounts">Export CSV</Button>
          </div>
        </div>
        <div className="mobile-cards overflow-x-auto rounded-2xl border border-border bg-card scrollbar-thin">
          <Table data-testid="accounts-table">
            <TableHeader><TableRow>
              <TableHead>Username</TableHead><TableHead>Nama</TableHead><TableHead>Kontak</TableHead>
              <TableHead>Saldo</TableHead><TableHead>Voucher</TableHead><TableHead>Status</TableHead>
              <TableHead>Terdaftar</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} className="text-xs">
                  <TableCell data-label="Username" className="font-mono font-medium">{r.username}</TableCell>
                  <TableCell data-label="Nama">{r.name}</TableCell>
                  <TableCell data-label="Kontak" className="font-mono">{r.phone || "-"}</TableCell>
                  <TableCell data-label="Saldo" className="font-mono">{rp(r.saldo)}</TableCell>
                  <TableCell data-label="Voucher" className="font-mono">{r.vouchers}</TableCell>
                  <TableCell data-label="Status">
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] ${r.status === "active" ? "border-emerald-500/40 text-emerald-400" : "border-red-500/40 text-red-400"}`}>{r.status}</span>
                  </TableCell>
                  <TableCell data-label="Terdaftar" className="font-mono">{dt(r.created_at)}</TableCell>
                  <TableCell data-label="" className="space-x-1 text-right cell-actions">
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => setEdit({ ...r, newPassword: "", _username0: r.username, _owner0: r.owner_admin_id || "" })} data-testid={`edit-${r.username}`}>Edit</Button>
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => setSaldo({ uid: r.id, amount: 0, note: "" })} data-testid={`saldo-${r.username}`}><Wallet className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="text-destructive" onClick={() => remove(r)} data-testid={`delete-${r.username}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {!rows.length && <TableRow><TableCell colSpan={8} className="py-10 text-center text-sm text-muted-foreground cell-empty">Belum ada data</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </TabsContent>

      <TabsContent value="add">
        <form onSubmit={create} className="max-w-xl space-y-5 rounded-2xl border border-border bg-card p-6" data-testid="account-form">
          <h3 className="font-display text-lg font-semibold">Pendaftaran {title} Baru</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Username" required value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="acc-username" />
            <Field label="Password" required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="acc-password" />
            <Field label="Nama" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="acc-name" />
            <Field label="No. WhatsApp" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="acc-phone" />
            <Field label="Saldo Awal" type="number" value={form.saldo} onChange={(e) => setForm({ ...form, saldo: e.target.value })} data-testid="acc-saldo" />
            {role !== "admin" && (
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Admin Pengelola</Label>
                <select
                  className="h-11 w-full rounded-xl border border-input bg-secondary/50 px-3 text-sm" data-testid="acc-owner-admin"
                  value={form.owner_admin_id} onChange={(e) => setForm({ ...form, owner_admin_id: e.target.value })}
                >
                  <option value="">— saya sendiri / otomatis —</option>
                  {admins.map((a) => <option key={a.id} value={a.id}>{a.username} {a.name ? `(${a.name})` : ""}</option>)}
                </select>
                <p className="text-[11px] text-muted-foreground">Akun ini hanya akan tampil & dikelola oleh admin tersebut.</p>
              </div>
            )}
            {role === "admin" && (
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Router yang dikelola</Label>
                <select
                  className="h-11 w-full rounded-xl border border-input bg-secondary/50 px-3 text-sm" data-testid="acc-router"
                  value={form.router_id} onChange={(e) => setForm({ ...form, router_id: e.target.value })}
                >
                  <option value="">— pilih router —</option>
                  {routers.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                </select>
              </div>
            )}
            {(role === "mitra" || role === "user") && (
              <PackagePicker
                packages={packages} role={role} value={form.allowed_packages}
                onChange={(v) => setForm({ ...form, allowed_packages: v })} testId="acc-packages"
              />
            )}
          </div>
          {(role === "mitra" || role === "user") && (
            <p className="text-[11px] text-muted-foreground">
              Hanya paket yang dicentang akan muncul di dasbor akun ini. Biarkan kosong agar mengikuti pengaturan tampil/sembunyi global di menu Setting.
            </p>
          )}
          <Button type="submit" disabled={busy} data-testid="acc-submit" className="rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1 h-4 w-4" /> Daftarkan</>}
          </Button>
        </form>
      </TabsContent>

      <TabsContent value="saldo">
        <div className="max-w-xl space-y-5 rounded-2xl border border-border bg-card p-6" data-testid="manual-saldo-form">
          <h3 className="font-display text-lg font-semibold">Tambah Saldo Manual</h3>
          <p className="text-xs text-muted-foreground">Pilih akun {title.toLowerCase()}, isi nominal (boleh minus untuk pengurangan), lalu simpan. Tercatat di riwayat transaksi.</p>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Akun {title}</Label>
            <select
              className="h-11 w-full rounded-xl border border-input bg-secondary/50 px-3 text-sm" data-testid="manual-saldo-account"
              value={saldo.uid || ""} onChange={(e) => setSaldo({ ...saldo, uid: e.target.value })}
            >
              <option value="">— pilih akun —</option>
              {rows.map((r) => <option key={r.id} value={r.id}>{r.username} — {rp(r.saldo)}</option>)}
            </select>
          </div>
          <Field label="Nominal" type="number" value={saldo.amount} onChange={(e) => setSaldo({ ...saldo, amount: e.target.value })} data-testid="manual-saldo-amount" />
          <Field label="Catatan" value={saldo.note} onChange={(e) => setSaldo({ ...saldo, note: e.target.value })} data-testid="manual-saldo-note" />
          <Button
            onClick={async () => { if (!saldo.uid) return toast.error("Pilih akun dulu"); await topup(); setTab("list"); }}
            disabled={busy} data-testid="manual-saldo-save"
            className="rounded-full bg-gradient-to-r from-emerald-500 to-cyan-600 text-white"
          >
            <Wallet className="mr-1 h-4 w-4" /> Simpan Saldo
          </Button>
        </div>
      </TabsContent>

      <TabsContent value="deposit">
        <div className="mobile-cards overflow-x-auto rounded-2xl border border-border bg-card scrollbar-thin">
          <Table data-testid="deposits-table">
            <TableHeader><TableRow><TableHead>Pemohon</TableHead><TableHead>Nominal</TableHead><TableHead>Catatan</TableHead><TableHead>Status</TableHead><TableHead>Waktu</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
            <TableBody>
              {deposits.map((d) => (
                <TableRow key={d.id} className="text-xs">
                  <TableCell data-label="Pemohon" className="font-mono">{d.username}</TableCell>
                  <TableCell data-label="Nominal" className="font-mono">{rp(d.amount)}</TableCell>
                  <TableCell data-label="Catatan">{d.note || "-"}</TableCell>
                  <TableCell data-label="Status"><span className={`rounded-full border px-2 py-0.5 text-[10px] ${d.status === "pending" ? "border-amber-500/40 text-amber-400" : d.status === "approved" ? "border-emerald-500/40 text-emerald-400" : "border-red-500/40 text-red-400"}`}>{d.status}</span></TableCell>
                  <TableCell data-label="Waktu" className="font-mono">{dt(d.created_at)}</TableCell>
                  <TableCell data-label="" className="space-x-1 text-right cell-actions">
                    {d.status === "pending" && (
                      <>
                        <Button size="sm" className="rounded-full bg-emerald-600 text-white" onClick={() => actDeposit(d, "approve")} data-testid={`approve-${d.id}`}>Setujui</Button>
                        <Button size="sm" variant="outline" className="rounded-full text-destructive" onClick={() => actDeposit(d, "reject")} data-testid={`reject-${d.id}`}>Tolak</Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!deposits.length && <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground cell-empty">Belum ada permintaan saldo</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </TabsContent>

      {/* Edit dialog */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display">Edit {edit?._username0 || edit?.username}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-4">
              <Field label="Username" value={edit.username || ""} onChange={(e) => setEdit({ ...edit, username: e.target.value })} data-testid="edit-username" />
              {String(edit.username || "").trim().toLowerCase() !== edit._username0 && (
                <p className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-3 text-[11px] text-amber-400" data-testid="edit-username-warning">
                  Username akan diubah dari <b className="font-mono">{edit._username0}</b> menjadi <b className="font-mono">{String(edit.username || "").trim().toLowerCase()}</b>. Akun tersebut harus login memakai username baru.
                </p>
              )}
              <Field label="Nama" value={edit.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="edit-name" />
              <Field label="No. WhatsApp" value={edit.phone || ""} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} data-testid="edit-phone" />
              <Field label="Password Baru (opsional)" type="password" value={edit.newPassword} onChange={(e) => setEdit({ ...edit, newPassword: e.target.value })} data-testid="edit-password" />
              {role === "admin" && (
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Router yang dikelola</Label>
                  <select
                    className="h-11 w-full rounded-xl border border-input bg-secondary/50 px-3 text-sm" data-testid="edit-router"
                    value={edit.router_id || ""} onChange={(e) => setEdit({ ...edit, router_id: e.target.value })}
                  >
                    <option value="">— tanpa router —</option>
                    {routers.map((r) => <option key={r.id} value={r.id}>{r.label} {r.host ? `(${r.host})` : ""}</option>)}
                  </select>
                  <p className="text-[11px] text-muted-foreground">Router yang dipilih otomatis menjadi milik admin ini, sehingga data hotspot/FTTH-nya terpisah dari admin lain.</p>
                </div>
              )}
              {role !== "admin" && isSuper && (
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Admin Pengelola</Label>
                  <select
                    className="h-11 w-full rounded-xl border border-input bg-secondary/50 px-3 text-sm" data-testid="edit-owner-admin"
                    value={edit.owner_admin_id || ""} onChange={(e) => setEdit({ ...edit, owner_admin_id: e.target.value })}
                  >
                    <option value="">— tidak diubah —</option>
                    {admins.map((a) => <option key={a.id} value={a.id}>{a.username} {a.name ? `(${a.name})` : ""}</option>)}
                  </select>
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Status</Label>
                <select className="h-11 w-full rounded-xl border border-input bg-secondary/50 px-3 text-sm" value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })} data-testid="edit-status">
                  <option value="active">active</option><option value="suspended">suspended</option>
                </select>
              </div>
              {(role === "mitra" || role === "user") && (
                <PackagePicker
                  packages={packages} role={role} value={edit.allowed_packages || []}
                  onChange={(v) => setEdit({ ...edit, allowed_packages: v })} testId="edit-packages"
                />
              )}
              <Button onClick={save} disabled={busy} data-testid="edit-save" className="w-full rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white">Simpan</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Saldo dialog */}
      <Dialog open={!!saldo.uid} onOpenChange={(o) => !o && setSaldo({ uid: null, amount: 0, note: "" })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display">Tambah / Kurangi Saldo</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Nominal (boleh minus)" type="number" value={saldo.amount} onChange={(e) => setSaldo({ ...saldo, amount: e.target.value })} data-testid="saldo-amount" />
            <Field label="Catatan" value={saldo.note} onChange={(e) => setSaldo({ ...saldo, note: e.target.value })} data-testid="saldo-note" />
            <Button onClick={topup} disabled={busy} data-testid="saldo-save" className="w-full rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white">Simpan</Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Impor akun dari CSV */}
      <Dialog open={imp.open} onOpenChange={(o) => setImp((c) => ({ ...c, open: o }))}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="import-accounts-dialog">
          <DialogHeader><DialogTitle className="font-display">Impor {title} dari CSV</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="rounded-xl border border-border bg-secondary/30 p-3 text-[11px] leading-relaxed text-muted-foreground">
              Kolom yang dikenali: <b className="text-foreground">username, password, nama, no_wa, saldo, status, admin_pengelola, paket_voucher</b>.
              Hanya <b className="text-foreground">username</b> yang wajib; password kosong akan dibuat otomatis dan ditampilkan setelah impor.
              Paket voucher dipisah tanda <b className="text-foreground">|</b> (mis. 3jam|1hari).
              <Button size="sm" variant="outline" className="mt-2 h-7 rounded-full text-[11px]" onClick={downloadTemplate} data-testid="download-account-template">
                <FileText className="mr-1 h-3 w-3" /> Unduh template CSV
              </Button>
            </div>
            <input
              type="file" accept=".csv,text/csv" data-testid="account-csv-file"
              onChange={(e) => { const f = e.target.files?.[0] || null; setImp((c) => ({ ...c, file: f, result: null, preview: null })); previewCsv(f); }}
              className="block w-full cursor-pointer rounded-xl border border-border bg-secondary/50 p-2.5 text-xs file:mr-3 file:rounded-lg file:border-0 file:bg-emerald-600 file:px-3 file:py-1.5 file:text-white"
            />
            <label className="flex items-center justify-between gap-3 rounded-xl border border-border p-3 text-xs">
              <span>Perbarui akun bila username sudah ada
                <span className="block text-[11px] text-muted-foreground">Jika tidak dicentang, username yang sudah ada akan dilewati</span>
              </span>
              <input type="checkbox" checked={imp.update} onChange={(e) => { setImp((c) => ({ ...c, update: e.target.checked })); previewCsv(imp.file, e.target.checked); }} data-testid="account-csv-update" className="h-4 w-4" />
            </label>
            {imp.previewing && <p className="text-[11px] text-muted-foreground">Membaca pratinjau…</p>}
            {imp.preview && !imp.result && (
              <div className="space-y-2" data-testid="account-csv-preview">
                <p className="text-xs font-medium">
                  Pratinjau: {imp.preview.total_rows} baris ·{" "}
                  <span className="text-emerald-500">{imp.preview.created} baru</span> ·{" "}
                  <span className="text-cyan-500">{imp.preview.updated} diperbarui</span> ·{" "}
                  <span className="text-muted-foreground">{imp.preview.skipped} dilewati</span>
                </p>
                <div className="max-h-52 overflow-auto rounded-xl border border-border">
                  <table className="w-full text-[11px]">
                    <thead className="sticky top-0 bg-secondary/80">
                      <tr className="text-left">
                        <th className="px-2 py-1">Brs</th><th className="px-2 py-1">Username</th><th className="px-2 py-1">Nama</th>
                        <th className="px-2 py-1">Saldo</th><th className="px-2 py-1">Paket</th><th className="px-2 py-1">Password</th><th className="px-2 py-1">Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(imp.preview.preview || []).map((r) => (
                        <tr key={r.row} className="border-t border-border/60">
                          <td className="px-2 py-1 font-mono">{r.row}</td>
                          <td className="px-2 py-1 font-mono">{r.username}</td>
                          <td className="px-2 py-1">{r.name}</td>
                          <td className="px-2 py-1 font-mono">{rp(r.saldo)}</td>
                          <td className="px-2 py-1">{(r.packages || []).join(", ") || "semua"}</td>
                          <td className="px-2 py-1 text-muted-foreground">{r.password}</td>
                          <td className="px-2 py-1">
                            <span className={`rounded-full px-2 py-0.5 text-[10px] ${r.action === "baru" ? "bg-emerald-500/15 text-emerald-500" : "bg-cyan-500/15 text-cyan-500"}`}>{r.action}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {!!(imp.preview.errors || []).length && (
                  <div className="max-h-32 space-y-1 overflow-y-auto rounded-xl border border-amber-500/30 bg-amber-500/5 p-2">
                    {imp.preview.errors.map((er, i) => (
                      <p key={i} className="text-[11px] text-amber-600 dark:text-amber-300">Baris {er.row} · {er.username}: {er.reason}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
            <Button onClick={importCsv} disabled={busy || !imp.file} data-testid="account-csv-submit" className="w-full rounded-full bg-gradient-to-r from-emerald-500 to-cyan-600 text-white">
              <Upload className="mr-1 h-4 w-4" /> {imp.preview && !imp.result ? "Simpan Impor Sekarang" : "Mulai Impor"}
            </Button>
            {imp.result && (
              <div className="space-y-2" data-testid="account-csv-result">
                <div className="grid grid-cols-4 gap-2 text-center">
                  {[["Baris", imp.result.total_rows], ["Baru", imp.result.created], ["Update", imp.result.updated], ["Lewat", imp.result.skipped]].map(([l, v]) => (
                    <div key={l} className="rounded-xl border border-border p-2">
                      <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{l}</p>
                      <p className="font-mono text-base font-semibold">{v}</p>
                    </div>
                  ))}
                </div>
                {!!(imp.result.generated_passwords || []).length && (
                  <div className="space-y-1 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-2">
                    <p className="text-[11px] font-medium text-emerald-500">Password otomatis (catat sekarang):</p>
                    {imp.result.generated_passwords.map((c) => (
                      <p key={c.username} className="font-mono text-[11px]">{c.username} : {c.password}</p>
                    ))}
                  </div>
                )}
                {!!(imp.result.errors || []).length && (
                  <div className="max-h-40 space-y-1 overflow-y-auto rounded-xl border border-amber-500/30 bg-amber-500/5 p-2">
                    {imp.result.errors.map((er, i) => (
                      <p key={i} className="text-[11px] text-amber-600 dark:text-amber-300">Baris {er.row} · {er.username}: {er.reason}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Tabs>
  );
}
